import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, RotateCcw, FileText, Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useToast } from '../components/ui/use-toast';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const KilledDeals = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | killed | declined
  const [actionFor, setActionFor] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('mcaToken');
      const res = await axios.get(`${API_BASE}/deals/killed-declined`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeals(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.detail || err.response?.data?.error || 'Failed to load',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const restore = async (deal) => {
    if (!window.confirm(`Restore deal #${deal.id} (${deal.business || 'Unnamed'}) back to Submitted Deals?`)) return;
    try {
      setActionFor(deal.id);
      const token = localStorage.getItem('mcaToken');
      await axios.post(`${API_BASE}/deals/${deal.id}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Restored', description: `Deal #${deal.id} back in Submitted Deals` });
      load();
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.detail || err.response?.data?.error || 'Failed to restore',
        variant: 'destructive'
      });
    } finally {
      setActionFor(null);
    }
  };

  const permanentlyDelete = async (deal) => {
    const typed = window.prompt(
      `PERMANENTLY DELETE deal #${deal.id} (${deal.business || 'Unnamed'}) and ALL attached documents.\n\nThis cannot be undone.\n\nType "DELETE" to confirm:`
    );
    if (typed !== 'DELETE') {
      if (typed != null) toast({ title: 'Not deleted', description: 'You must type DELETE exactly.' });
      return;
    }
    try {
      setActionFor(deal.id);
      const token = localStorage.getItem('mcaToken');
      await axios.delete(`${API_BASE}/deals/${deal.id}/permanent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Deleted', description: `Deal #${deal.id} and its documents are gone` });
      load();
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.detail || err.response?.data?.error || 'Failed to delete',
        variant: 'destructive'
      });
    } finally {
      setActionFor(null);
    }
  };

  const filtered = deals.filter(d => {
    if (filter !== 'all' && d.status !== filter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    const hits = [
      d.business, d.business_name, String(d.id),
      d.client?.name, d.client?.contact_name, d.client?.email, d.client?.first_name, d.client?.last_name
    ].filter(Boolean).join(' ').toLowerCase();
    return hits.includes(q);
  });

  const statusPill = (status) => {
    if (status === 'killed') return <span className="rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-xs font-semibold">Killed</span>;
    if (status === 'declined') return <span className="rounded-full bg-orange-100 text-orange-700 px-2 py-0.5 text-xs font-semibold">Declined</span>;
    return <span className="rounded-full bg-gray-100 text-gray-700 px-2 py-0.5 text-xs">{status}</span>;
  };

  const counts = {
    all: deals.length,
    killed: deals.filter(d => d.status === 'killed').length,
    declined: deals.filter(d => d.status === 'declined').length,
  };

  return (
    <div className="p-6" data-testid="killed-deals-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Killed & Declined Deals</h1>
          <p className="text-sm text-gray-500 mt-1">Admin-only — contains every killed/declined client record, including attached documents. Restore or permanently delete from here.</p>
        </div>
        <div className="flex items-center gap-2">
          {['all', 'killed', 'declined'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
              className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                filter === f ? 'bg-gray-900 text-white' : 'bg-white border text-gray-700 hover:bg-gray-50'
              }`}
            >
              {f === 'all' ? 'All' : f === 'killed' ? 'Killed' : 'Declined'} ({counts[f]})
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-4">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search by business, client name, email, or deal #"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="killed-search"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        {loading ? (
          <p className="p-6 text-center text-gray-500">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="p-10 text-center text-gray-400 italic">No killed or declined deals.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left font-semibold text-gray-700">#</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Business</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Contact</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Email</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Phone</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">Funded</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">Payback</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Docs</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Date</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((deal) => {
                const c = deal.client || {};
                const contact = c.contact_name
                  || `${c.first_name || ''} ${c.last_name || ''}`.trim()
                  || deal.contact_name
                  || '—';
                const biz = deal.business || deal.business_name || c.business_name || c.name || '—';
                const email = c.email || deal.email || '—';
                const phone = c.phone || c.business_phone || deal.phone || '—';
                const killedAt = deal.killed_at || deal.declined_at || deal.date;
                const when = killedAt ? new Date(killedAt).toLocaleDateString() : '—';
                const busy = actionFor === deal.id;
                return (
                  <tr key={deal.id} className="border-b border-gray-100 hover:bg-orange-50 transition" data-testid={`killed-row-${deal.id}`}>
                    <td className="px-4 py-3 text-gray-700">{deal.display_number ?? deal.id}</td>
                    <td className="px-4 py-3 text-gray-900 font-medium">{biz}</td>
                    <td className="px-4 py-3 text-gray-700">{contact}</td>
                    <td className="px-4 py-3 text-blue-600">{email}</td>
                    <td className="px-4 py-3 text-gray-600">{phone}</td>
                    <td className="px-4 py-3 text-right text-gray-900">${Number(deal.advanced || deal.funding_amount || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-gray-900">${Number(deal.payback || deal.payback_amount || 0).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-xs text-gray-600" title={(deal.documents || []).map(d => d.name).join(', ')}>
                        <FileText size={12} /> {deal.documents_count || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3">{statusPill(deal.status)}</td>
                    <td className="px-4 py-3 text-gray-600">{when}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          className="text-xs text-gray-600 underline hover:text-gray-900"
                          onClick={() => navigate(`/deals/${deal.display_number ?? deal.id}`)}
                          data-testid={`view-killed-${deal.id}`}
                        >
                          View
                        </button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs h-7 px-2 border-blue-500 text-blue-600 hover:bg-blue-50"
                          disabled={busy}
                          onClick={() => restore(deal)}
                          data-testid={`restore-${deal.id}`}
                        >
                          <RotateCcw size={12} className="mr-1" /> Restore
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs h-7 px-2 border-red-500 text-red-600 hover:bg-red-50"
                          disabled={busy}
                          onClick={() => permanentlyDelete(deal)}
                          data-testid={`permanent-delete-${deal.id}`}
                        >
                          <Trash2 size={12} className="mr-1" /> Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default KilledDeals;
